import { Button } from "@bandwith/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, FilePlus } from "lucide-react";
import {
  PrevNextNav,
  useSetlistNavigation,
} from "../components/SetlistNavigation";
import { api } from "../lib/api";
import { useSession } from "../lib/auth";
import { setlistQuery } from "../lib/queries";
import { useSignInRedirect } from "../lib/useSignInRedirect";
import { setlistEntryRoute } from "../router";

/**
 * The page for a setlist entry that's just a title (a song not kept in
 * Bandwith), so paging through a setlist stops on it. "Add Chart" gives it a
 * cover song of its own and opens that song's editor.
 */
export function SetlistEntry() {
  const { id: setlistId, entryId } = setlistEntryRoute.useParams();
  const from = `setlist-${setlistId}`;
  const { session } = useSession();
  const signInRedirect = useSignInRedirect();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const navigation = useSetlistNavigation({ entryId }, from);

  const { data: setlist, isLoading } = useQuery(setlistQuery(setlistId));
  const entry = setlist?.songs.find((e) => e.id === entryId);

  const addChart = useMutation({
    mutationFn: async () => {
      const res = await api.setlists[":id"].entries[":entryId"].cover.$post({
        param: { id: setlistId, entryId },
      });
      if (res.status === 404) {
        // Either someone added a chart first or the setlist isn't ours to
        // edit. Refetch: if the entry now has a song, the redirect below
        // takes over and this message never shows.
        await queryClient.invalidateQueries({
          queryKey: setlistQuery(setlistId).queryKey,
        });
        throw new Error("Only the setlist's creator can add a chart here.");
      }
      if (!res.ok) throw new Error("Failed to add a chart");
      return res.json();
    },
    onSuccess: async ({ songId }) => {
      await queryClient.invalidateQueries({
        queryKey: setlistQuery(setlistId).queryKey,
      });
      navigate({
        to: "/songs/$id",
        params: { id: songId },
        search: { action: "edit", from },
      });
    },
  });

  const handleAddChart = () => {
    if (!session?.user) {
      signInRedirect(`/setlists/${setlistId}/entries/${entryId}`);
      return;
    }
    addChart.mutate();
  };

  if (isLoading) return <div className="text-text-muted">Loading...</div>;
  if (!entry) {
    return (
      <div className="text-text-muted">This song isn't in the setlist.</div>
    );
  }
  // The entry got a song since this link was made: show the song instead
  if (entry.song) {
    return (
      <Navigate
        to="/songs/$id"
        params={{ id: entry.song.id }}
        search={{ from }}
        replace
      />
    );
  }

  return (
    <>
      <nav className="mb-8 flex flex-col gap-2">
        <Link
          to={navigation.href}
          className="group inline-flex items-center gap-2 text-sm text-text-secondary transition-colors hover:text-amber"
        >
          <span className="transition-transform duration-150 group-hover:-translate-x-1">
            <ChevronLeft className="h-4 w-4" />
          </span>
          {navigation.label}
        </Link>
        <PrevNextNav prev={navigation.prev} next={navigation.next} />
      </nav>

      <header className="mb-10">
        <h1 className="font-display text-3xl text-text-primary">
          {entry.title}
        </h1>
        <p className="mt-2 text-text-muted">
          Not in Bandwith. Add a chart to keep its chords and lyrics here.
        </p>
      </header>

      <Button
        onClick={handleAddChart}
        disabled={addChart.isPending}
        leftIcon={FilePlus}
      >
        {addChart.isPending ? "Adding..." : "Add Chart"}
      </Button>
      {addChart.error && (
        <p className="mt-2 text-sm text-red-muted">{addChart.error.message}</p>
      )}
    </>
  );
}
